import {useRef} from 'react';
import {Keyboard, Platform} from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import {
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {BottomDrawerMethods} from 'react-native-animated-bottom-drawer';
import * as MediaLibrary from 'expo-media-library';
import uuid from 'react-native-uuid';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system';
import {useDispatch} from 'react-redux';
import {useCameraPermissions} from 'expo-camera';
import {fileTypesApi, Media} from '../types/';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {setToastText} from '../store/slices/ui';
import {ChatParams, UnifiedStackParams} from '../types/nav';
import {startOnboarding} from '../store/slices/upload';
import {getUserData} from '../api';

export function useNavigateWithAsset(
  folderId: string,
  isChatMedia?: true,
) {
  const dispatch = useAppDispatch();
  const navigateWithAsset = async (
    asset: DocumentPicker.DocumentPickerAsset,
  ) => {
    const url = asset.uri;
    const user = await getUserData();
    const mimeType =
      asset.file?.type ||
      asset.fileType?.toLowerCase() ||
      asset.mimeType ||
      asset.type;
    let name = asset.fileName || asset.name;
    if (name.search('.') === 0 && Platform.OS !== 'web') {
      const splitUri = asset.uri.split('/');
      name = splitUri[splitUri.length - 1];
      console.info('splitUri', splitUri);
    }
    console.info('mimeType', mimeType, asset, name, name.search('.'));
    // const file
    const nd: Media = {
      id: uuid.v4().toString(),
      url,
      title: name,
      originalId: name,
      filename: name,
      fileType: mimeType,
      folderId,
      userId: user.id,
      date: new Date().valueOf(),
      isMediaNew: true,
      // isMediaNew: !isEncoderProject,
      // isEncoderProject,
      type: mimeType,
      size: asset.file?.size || asset.size,
      // extractor: 'wt',
      isChatMedia,
    };
    dispatch(startOnboarding({file: asset.file || asset, media: nd}));
  };

  return navigateWithAsset;
}

export function useFileSelectOptions(
  chatId: string,
  isChat?: true,
  isGlobal?: true,
) {
  const nav = useNavigation<NavigationProp<UnifiedStackParams>>();
  const [cameraPermission, requestCameraPermission] =
    useCameraPermissions();
  const [libraryPermission, requestLibraryPermission] =
    MediaLibrary.usePermissions();
  const bottomDrawerRef = useRef<BottomDrawerMethods>(null);
  const dispatch = useDispatch();
  const navigateWithAsset = useNavigateWithAsset(chatId, isChat);
  const routeParams = useRoute().params as ChatParams;
  const storeParams = useAppSelector(state => state.ui.params);
  let params = isGlobal ? storeParams : routeParams;
  const parentName = useAppSelector(state => state.ui.parentName);
  const showDrawer = () => {
    bottomDrawerRef.current?.open();
    Keyboard.dismiss();
  };

  const hideDrawer = () => {
    bottomDrawerRef.current?.close();
    Keyboard.dismiss();
  };

  const selectFile = async () => {
    try {
      if (Platform.OS === 'android') {
        await FileSystem.StorageAccessFramework.requestDirectoryPermissionsAsync();
      }

      const picker = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: true,
        multiple: false,
        type: Object.keys(fileTypesApi),
      });

      if (picker.canceled || !picker.assets.length) {
        dispatch(setToastText('Nothing selected'));
        hideDrawer();

        return;
      }

      const [asset] = picker.assets;
      navigateWithAsset(asset);
    } catch (err) {
      const errMessage = (err as Error)?.message || err;
      dispatch(
        setToastText(`Error on selecting document: ${errMessage}`),
      );
    }
    hideDrawer();
  };

  const selectImage = async () => {
    try {
      let res: MediaLibrary.PermissionResponse | null = null;
      if (libraryPermission?.granted === false) {
        res = await requestLibraryPermission();
      }

      if (
        res?.granted === false &&
        libraryPermission?.granted === false
      ) {
        dispatch(
          setToastText('Please grant permission for photo library'),
        );
        hideDrawer();

        return;
      }
      const picker = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        quality: 0.5,
        videoQuality:
          ImagePicker.UIImagePickerControllerQualityType.Low,
      });
      console.info('picker res', picker);
      if (picker.canceled || !picker.assets.length) {
        dispatch(setToastText('Nothing selected'));
        hideDrawer();

        return;
      }

      const [photo] = picker.assets;
      const asset = photo as typeof photo & {
        name: string;
        size: number;
      };
      asset.name = `Photo_${uuid.v4()}`;
      asset.size = photo.fileSize as number;
      navigateWithAsset(asset);
    } catch (err) {
      const errMessage = (err as Error)?.message || err;
      dispatch(
        setToastText(`Error on selecting image: ${errMessage}`),
      );
    }
    hideDrawer();
  };

  const makePhoto = async () => {
    try {
      let res: MediaLibrary.EXPermissionResponse | null = null;
      if (cameraPermission?.granted === false) {
        res = await requestCameraPermission();
      }

      if (
        res?.granted === false &&
        cameraPermission?.granted === false
      ) {
        dispatch(setToastText('Please grant permission for camera'));
        hideDrawer();

        return;
      }

      nav.navigate(parentName, {screen: 'Camera', params});
    } catch (err) {
      const errMessage = (err as Error)?.message || err;
      dispatch(
        setToastText(`Error on selecting camera: ${errMessage}`),
      );
    }
    hideDrawer();
  };

  return {
    makePhoto,
    selectImage,
    selectFile,
    showDrawer,
    bottomDrawerRef,
  };
}
